import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 2025</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using Transplacement ("the Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform. These Terms constitute a legally binding agreement between you and Transplacement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              Transplacement is a platform that provides transparent, verified placement data from colleges and universities. Users can submit their placement information, which is verified and aggregated to provide insights into compensation trends and college rankings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">3. User Accounts</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">3.1 Registration</h3>
                <p className="text-muted-foreground leading-relaxed">
                  To submit placement data, you must create an account using Google Sign-In. You are responsible for maintaining the confidentiality of your account and for all activities that occur under your account.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">3.2 Account Requirements</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>You must be at least 18 years old to use this Platform</li>
                  <li>You must provide accurate and complete information</li>
                  <li>You must not create multiple accounts or impersonate others</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">4. User Submissions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">4.1 Accuracy of Information</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You agree that all placement information you submit is accurate, truthful, and verifiable. Submitting false or misleading information is strictly prohibited and may result in account termination.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">4.2 Verification</h3>
                <p className="text-muted-foreground leading-relaxed">
                  You consent to our verification processes, which may include reviewing offer letters, checking LinkedIn profiles, and other methods to ensure data accuracy.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">4.3 License to Use</h3>
                <p className="text-muted-foreground leading-relaxed">
                  By submitting data, you grant Transplacement a non-exclusive, worldwide, royalty-free license to use, aggregate, anonymize, and display your submission data for the purposes of operating the Platform.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">5. Prohibited Conduct</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Submit false, misleading, or fraudulent placement information</li>
              <li>Attempt to manipulate rankings or statistics</li>
              <li>Scrape, harvest, or collect data from the Platform without permission</li>
              <li>Use automated systems to access the Platform</li>
              <li>Interfere with or disrupt the Platform's operation</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Attempt to gain unauthorized access to any part of the Platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">6. Intellectual Property</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">6.1 Platform Content</h3>
                <p className="text-muted-foreground leading-relaxed">
                  All content on the Platform, including text, graphics, logos, and software, is the property of Transplacement or its licensors and is protected by intellectual property laws.
                </p>
              </div>
              <div>
                <h3 className="font-medium mb-2">6.2 Limited License</h3>
                <p className="text-muted-foreground leading-relaxed">
                  We grant you a limited, non-exclusive, non-transferable license to access and use the Platform for personal, non-commercial purposes.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">7. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground leading-relaxed">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE. WHILE WE STRIVE FOR DATA ACCURACY, WE DO NOT GUARANTEE THE ACCURACY, COMPLETENESS, OR RELIABILITY OF ANY PLACEMENT DATA.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">8. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRANSPLACEMENT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE PLATFORM, INCLUDING BUT NOT LIMITED TO DECISIONS MADE BASED ON PLACEMENT DATA.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">9. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">
              You agree to indemnify, defend, and hold harmless Transplacement and its affiliates from any claims, damages, losses, or expenses arising from your use of the Platform, your submissions, or your violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We reserve the right to suspend or terminate your account at any time, with or without cause, including for violation of these Terms. Upon termination, your right to use the Platform will immediately cease.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">11. Modifications to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms at any time. We will notify users of material changes by updating the "Last updated" date. Your continued use of the Platform after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">12. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of India.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">13. Severability</h2>
            <p className="text-muted-foreground leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-4">14. Contact Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about these Terms, please contact us at:
            </p>
            <p className="text-muted-foreground mt-2">
              Email: <a href="mailto:legal@transplacement.com" className="text-primary hover:underline">legal@transplacement.com</a>
            </p>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          <span>•</span>
          <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
        </div>
      </main>
    </div>
  );
}
